import { TableSortLabel, styled } from "@mui/material";
import { rem } from "polished";
import React from "react";

import { ariaLabel } from "src/constants";
import { SortDirectionEnum, SortByType } from "src/graphql/generated/types";

import { ListColumnsConfig } from "../index";
import { ListSelectionCheckbox } from "./SelectionCheckbox";

type Props<T = any> = {
  isMaxSelectedReached: boolean;
  columnsConfig: ListColumnsConfig<T>;
  toggleAll: (checked: boolean) => void;
  areAllRowSelected: boolean;
  // Sorting
  activeSort?: SortByType;
  onSort?: (newSort: SortByType) => void;
  defaultSortDirection: SortDirectionEnum;
};

const StyledHeader = styled("tr")<Pick<Props, "columnsConfig">>`
  background: ${(props) => props.theme.palette.primary.main};
  color: ${(props) => props.theme.palette.primary.contrastText};
  width: 100%;

  > th .MuiIconButton-label {
    color: ${(props) => props.theme.palette.primary.contrastText};
  }

  ${(props) =>
    props.columnsConfig.map((c, index) => {
      // Column Width
      let widthValue = "auto";
      if (c.type === "selection" || c.type === "expand") {
        widthValue = "4%";
      } else if (c.width) {
        widthValue = `${c.width}%`;
      }

      // TextAlign
      let textAlign = "left";
      if (c.headerAlign) {
        textAlign = c.headerAlign;
      }

      return `& th:nth-of-type(${index + 1}) {
        width: ${widthValue};
        text-align: ${textAlign};
      }`;
    })}

  > th {
    padding: ${(props) =>
      props.theme.spacing(props.columnsConfig.find((c) => c.type === "selection") ? 1 : 2)};
    font-weight: bold;
    font-size: ${rem(13)};
  }
`;

const StyledTableSortLabel = styled(TableSortLabel)`
  &.MuiButtonBase-root.MuiTableSortLabel-root {
    color: white;

    & svg.MuiTableSortLabel-icon {
      fill: white;
      opacity: 0.3;
    }

    &:hover svg.MuiTableSortLabel-icon {
      opacity: 0.6;
    }

    &.MuiTableSortLabel-active svg.MuiTableSortLabel-icon {
      opacity: 1;
    }

    &.Mui-focusVisible {
      outline: 2px solid ${(props) => props.theme.palette.blue.focus};
    }
  }
`;

function convertDirectionToMUI(direction: SortDirectionEnum): "asc" | "desc" {
  return direction === SortDirectionEnum.Asc ? "asc" : "desc";
}

export const ListDefaultHeader = React.memo(
  ({
    isMaxSelectedReached,
    columnsConfig,
    toggleAll,
    areAllRowSelected,
    activeSort,
    onSort,
    defaultSortDirection,
  }: Props) => {
    return (
      <StyledHeader columnsConfig={columnsConfig} aria-label={ariaLabel.columnsHeadersRow}>
        {columnsConfig.map((c, index) => {
          let content = null;

          if (c.type === "selection") {
            content = (
              <ListSelectionCheckbox
                isMaxSelectedReached={isMaxSelectedReached}
                checked={areAllRowSelected}
                onChange={toggleAll}
                id="checkbox-select-all"
              />
            );
          } else if (c.type === "expand") {
            content = null;
          } else if (activeSort && onSort && c.sortableKey) {
            const isActiveColumn = activeSort.key === c.sortableKey;
            let nextDirection: SortDirectionEnum = defaultSortDirection;
            let directionLabel =
              defaultSortDirection === SortDirectionEnum.Asc
                ? ariaLabel.sortDirectionAscLabel
                : ariaLabel.sortDirectionDescLabel;
            if (isActiveColumn) {
              const isAsc = activeSort.direction === SortDirectionEnum.Asc;
              nextDirection = isAsc ? SortDirectionEnum.Desc : SortDirectionEnum.Asc;
              directionLabel = isAsc
                ? ariaLabel.sortDirectionAscLabel
                : ariaLabel.sortDirectionDescLabel;
            }

            content = (
              <StyledTableSortLabel
                onClick={() => {
                  if (!c.sortableKey) {
                    return;
                  }

                  onSort({
                    key: c.sortableKey,
                    direction: nextDirection,
                  });
                }}
                active={isActiveColumn}
                direction={convertDirectionToMUI(
                  isActiveColumn ? activeSort.direction : nextDirection
                )}
                hideSortIcon={false}
                aria-label={`${c.label} ${directionLabel}`}
              >
                {c.label}
              </StyledTableSortLabel>
            );
          } else {
            content = c.label;
          }

          const isEmptyContent = content === null || content === "";

          return (
            <th key={index} aria-hidden={isEmptyContent ? true : undefined}>
              {content}
            </th>
          );
        })}
      </StyledHeader>
    );
  }
);
